import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import Modal from '../../components/Modal';
import ApiInspector from '../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../components/BopRowActions';
import PrintModal from '../../components/PrintModal';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import { formatCurrency } from '../../utils/formatters';
import { API_DOCS } from './MovimentiContabAnaliticaPage.api';
import './MovimentiContabAnaliticaPage.css';

const MODULO = 'controllo_gestione.movimenti_analitici';

type Natura = 'COSTO' | 'RICAVO';

interface Movimento {
  id: number;
  societaId: number;
  contoAnaliticoId: number;
  contoCodice: string | null;
  contoDescrizione: string | null;
  natura: Natura | null;
  data: string;
  importo: number;
  competenzaDal: string | null;
  competenzaAl: string | null;
  descrizione: string | null;
  numeroDocumento: string | null;
  commessaId: number | null;
  commessaNumero: string | null;
  commessaDescrizione: string | null;
  origine: 'MANUALE' | 'AUTOMATICO';
  origineTipo: string | null;
  origineId: number | null;
}

interface ContoNode {
  id: number;
  codice: string;
  descrizione: string;
  natura: Natura;
  attivo: boolean;
  figli: ContoNode[];
}

interface ContoLeaf { id: number; codice: string; descrizione: string; natura: Natura; }
interface Commessa { id: number; numero: string; descrizione: string; }
interface Divisione { id: number; codice: string; descrizione: string; attivo: boolean; }

interface FormState {
  societaId: string;
  contoAnaliticoId: string;
  data: string;
  importo: string;
  competenzaDal: string;
  competenzaAl: string;
  descrizione: string;
  numeroDocumento: string;
  commessaId: string;
}

const oggiISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = (societaId = ''): FormState => ({
  societaId,
  contoAnaliticoId: '',
  data: oggiISO(),
  importo: '',
  competenzaDal: '',
  competenzaAl: '',
  descrizione: '',
  numeroDocumento: '',
  commessaId: '',
});

const fmtData = (d: string | null) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtCompetenza = (dal: string | null, al: string | null) => {
  if (!dal && !al) return '—';
  return `${fmtData(dal)} – ${fmtData(al)}`;
};

export default function MovimentiContabAnaliticaPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const canDelete = can(MODULO, 'delete');

  const { societaId, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  // Filtri iniziali dalla query string (deep-link dal bilancio analitica)
  const [searchParams] = useSearchParams();

  const [movimenti, setMovimenti] = useState<Movimento[]>([]);
  const [contiLeaves, setContiLeaves] = useState<ContoLeaf[]>([]);
  const [commesse, setCommesse] = useState<Commessa[]>([]);
  const [divisioni, setDivisioni] = useState<Divisione[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filtroDataDal, setFiltroDataDal] = useState(() => searchParams.get('dataDal') || '');
  const [filtroDataAl, setFiltroDataAl] = useState(() => searchParams.get('dataAl') || '');
  const [filtroNatura, setFiltroNatura] = useState(() => searchParams.get('natura') || '');
  const [filtroContoId, setFiltroContoId] = useState(() => searchParams.get('contoAnaliticoId') || '');
  const [filtroDivisioneId, setFiltroDivisioneId] = useState(() => searchParams.get('divisioneId') || '');

  // SUPERADMIN con topbar "Tutte le società": applica la società del deep-link
  // (per società fissa/operativo la topbar la determina già da localStorage).
  useEffect(() => {
    const sid = searchParams.get('societaId');
    if (sid && !societaId) setSocietaId(sid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [stampaOpen, setStampaOpen] = useState(false);

  // ── Caricamento conti foglia + commesse + divisioni (per filtri e modale) ──
  const loadAux = async () => {
    if (!societaId) { setContiLeaves([]); setCommesse([]); setDivisioni([]); return; }
    try {
      // @api GET /api/conti-analitici?societaId= -> { success: true, data: [{ id, codice, descrizione, natura, attivo, figli }] }
      const contiRes = await apiFetch<{ success: boolean; data: ContoNode[] }>(
        `/api/conti-analitici?societaId=${societaId}`,
      );
      const leaves: ContoLeaf[] = [];
      const walk = (nodes: ContoNode[]) => {
        for (const n of nodes) {
          if (n.figli.length === 0) {
            if (n.attivo) leaves.push({ id: n.id, codice: n.codice, descrizione: n.descrizione, natura: n.natura });
          } else {
            walk(n.figli);
          }
        }
      };
      walk(contiRes.data ?? []);
      leaves.sort((a, b) => a.codice.localeCompare(b.codice, undefined, { numeric: true }));
      setContiLeaves(leaves);

      // @api GET /api/commesse?societaId= -> { commesse: [{ id, numero, descrizione }] }
      const commRes = await apiFetch<{ commesse: Commessa[] }>(`/api/commesse?societaId=${societaId}`);
      setCommesse(commRes.commesse ?? []);

      // @api GET /api/divisioni-analitiche?societaId= -> { success: true, data: [{ id, codice, descrizione, attivo }] }
      const divRes = await apiFetch<{ success: boolean; data: Divisione[] }>(
        `/api/divisioni-analitiche?societaId=${societaId}`,
      );
      setDivisioni(divRes.data ?? []);
    } catch {
      // Conti/commesse/divisioni non bloccanti: l'elenco movimenti resta utilizzabile
      setContiLeaves([]);
      setCommesse([]);
      setDivisioni([]);
    }
  };

  const load = async () => {
    if (!societaId) { setMovimenti([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('societaId', societaId);
      if (filtroDataDal) params.set('dataDal', filtroDataDal);
      if (filtroDataAl) params.set('dataAl', filtroDataAl);
      if (filtroNatura) params.set('natura', filtroNatura);
      if (filtroContoId) params.set('contoAnaliticoId', filtroContoId);
      if (filtroDivisioneId) params.set('divisioneId', filtroDivisioneId);
      // @api GET /api/movimenti-conto-analitico?societaId=&dataDal=&dataAl=&natura=&contoAnaliticoId=&divisioneId= -> { success: true, data: [{ id, contoCodice, contoDescrizione, natura, data, importo, competenzaDal, competenzaAl, descrizione, numeroDocumento, commessaNumero, origine }] }
      const res = await apiFetch<{ success: boolean; data: Movimento[] }>(
        `/api/movimenti-conto-analitico?${params}`,
      );
      setMovimenti(res.data ?? []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAux(); }, [societaId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [societaId, filtroDataDal, filtroDataAl, filtroNatura, filtroContoId, filtroDivisioneId]);

  // ── Totali ─────────────────────────────────────────────────────────────────
  const totali = useMemo(() => {
    let costi = 0;
    let ricavi = 0;
    for (const m of movimenti) {
      if (m.natura === 'COSTO') costi += m.importo;
      else if (m.natura === 'RICAVO') ricavi += m.importo;
    }
    return { costi, ricavi, saldo: ricavi - costi };
  }, [movimenti]);

  // ── Modale ──────────────────────────────────────────────────────────────────
  const openCreate = () => {
    setForm(emptyForm(societaId || ''));
    setEditId(null);
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (m: Movimento) => {
    setForm({
      societaId: String(m.societaId),
      contoAnaliticoId: String(m.contoAnaliticoId),
      data: m.data.slice(0, 10),
      importo: String(m.importo),
      competenzaDal: m.competenzaDal ? m.competenzaDal.slice(0, 10) : '',
      competenzaAl: m.competenzaAl ? m.competenzaAl.slice(0, 10) : '',
      descrizione: m.descrizione ?? '',
      numeroDocumento: m.numeroDocumento ?? '',
      commessaId: m.commessaId ? String(m.commessaId) : '',
    });
    setEditId(m.id);
    setFormError('');
    setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setFormError(''); };

  const save = async () => {
    const sid = form.societaId ? Number(form.societaId) : 0;
    if (!sid) { setFormError('Seleziona una società'); return; }
    if (!form.contoAnaliticoId) { setFormError('Seleziona un conto'); return; }
    if (!form.data) { setFormError('Data obbligatoria'); return; }
    const importoNum = Number(form.importo.replace(',', '.'));
    if (!Number.isFinite(importoNum)) { setFormError('Importo non valido'); return; }
    if (importoNum === 0) { setFormError('L\'importo non può essere zero'); return; }
    if ((form.competenzaDal && !form.competenzaAl) || (!form.competenzaDal && form.competenzaAl)) {
      setFormError('Indica entrambe le date di competenza oppure nessuna');
      return;
    }
    if (form.competenzaDal && form.competenzaAl && new Date(form.competenzaAl) < new Date(form.competenzaDal)) {
      setFormError('La competenza "al" deve essere >= "dal"');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      const payload = {
        contoAnaliticoId: Number(form.contoAnaliticoId),
        data: form.data,
        importo: importoNum,
        competenzaDal: form.competenzaDal || null,
        competenzaAl: form.competenzaAl || null,
        descrizione: form.descrizione.trim() || null,
        numeroDocumento: form.numeroDocumento.trim() || null,
        commessaId: form.commessaId ? Number(form.commessaId) : null,
      };
      if (editId) {
        // @api PUT /api/movimenti-conto-analitico/:id { contoAnaliticoId, data, importo, competenzaDal, competenzaAl, descrizione, numeroDocumento, commessaId } -> { success: true, data: { id } }
        await apiFetch(`/api/movimenti-conto-analitico/${editId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        // @api POST /api/movimenti-conto-analitico { societaId, contoAnaliticoId, data, importo, competenzaDal, competenzaAl, descrizione, numeroDocumento, commessaId } -> { success: true, data: { id } }
        await apiFetch('/api/movimenti-conto-analitico', {
          method: 'POST',
          body: JSON.stringify({ ...payload, societaId: sid }),
        });
      }
      closeModal();
      load();
    } catch (e: any) {
      setFormError(e.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const elimina = async (m: Movimento) => {
    if (!window.confirm(`Eliminare il movimento del ${fmtData(m.data)} su ${m.contoCodice} (${formatCurrency(m.importo)})?`)) return;
    try {
      // @api DELETE /api/movimenti-conto-analitico/:id -> { success: true }
      await apiFetch(`/api/movimenti-conto-analitico/${m.id}`, { method: 'DELETE' });
      load();
    } catch (e: any) {
      window.alert(e.message || 'Errore nell\'eliminazione');
    }
  };

  const noSocieta = !societaId;
  const contoSelezionato = contiLeaves.find(c => String(c.id) === form.contoAnaliticoId);

  return (
    <Layout>
      <div className="movimenti-contab-analitica">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>Movimenti contabilità analitica</h2>
            <p>Visualizzazione e inserimento manuale dei movimenti</p>
          </div>
          <div className="bop-flex">
            <button
              className="bop-btn bop-btn-secondary"
              onClick={() => setStampaOpen(true)}
              disabled={noSocieta}
              title="Stampa / PDF dei movimenti filtrati"
            >
              Stampa
            </button>
            {canEdit && (
              <button
                className="bop-btn bop-btn-primary"
                onClick={openCreate}
                disabled={noSocieta || contiLeaves.length === 0}
                title={contiLeaves.length === 0 ? 'Crea prima il piano dei conti (voci foglia)' : undefined}
              >
                + Nuovo movimento
              </button>
            )}
          </div>
        </div>

        <div className="bop-filter-bar">
          {!isUnica && (
            <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
              <option value="">Seleziona società…</option>
              {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          )}
          <label className="mca-filter-field">
            <span className="mca-filter-label">Dal</span>
            <input className="bop-input" type="date" value={filtroDataDal} onChange={e => setFiltroDataDal(e.target.value)} />
          </label>
          <label className="mca-filter-field">
            <span className="mca-filter-label">Al</span>
            <input className="bop-input" type="date" value={filtroDataAl} onChange={e => setFiltroDataAl(e.target.value)} />
          </label>
          <select className="bop-select" value={filtroDivisioneId} onChange={e => setFiltroDivisioneId(e.target.value)} disabled={noSocieta}>
            <option value="">Tutte le divisioni</option>
            {divisioni.map(d => <option key={d.id} value={d.id}>{d.codice} — {d.descrizione}</option>)}
          </select>
          <select className="bop-select" value={filtroNatura} onChange={e => setFiltroNatura(e.target.value)}>
            <option value="">Tutte le nature</option>
            <option value="COSTO">Costi</option>
            <option value="RICAVO">Ricavi</option>
          </select>
          <select className="bop-select" value={filtroContoId} onChange={e => setFiltroContoId(e.target.value)} disabled={noSocieta}>
            <option value="">Tutti i conti</option>
            {contiLeaves.map(c => <option key={c.id} value={c.id}>{c.codice} — {c.descrizione}</option>)}
          </select>
        </div>

        {error && <div className="bop-inline-error">{error}</div>}

        <div className="bop-card">
          <div className="bop-table-wrapper">
            {noSocieta ? (
              <div className="bop-empty-state">Seleziona una società per vedere i movimenti.</div>
            ) : loading ? (
              <div className="bop-empty-state">Caricamento…</div>
            ) : movimenti.length === 0 ? (
              <div className="bop-empty-state">Nessun movimento trovato.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Conto</th>
                    <th>Descrizione</th>
                    <th>Commessa</th>
                    <th>Competenza</th>
                    <th>Natura</th>
                    <th className="bop-text-right">Importo</th>
                    <th className="bop-text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {movimenti.map(m => {
                    const isManuale = m.origine === 'MANUALE';
                    return (
                      <tr key={m.id}>
                        <td>{fmtData(m.data)}</td>
                        <td>
                          <span className="mca-conto-codice">{m.contoCodice}</span>{' '}
                          <span className="bop-text-muted">{m.contoDescrizione}</span>
                          {!isManuale && <span className="bop-badge bop-badge--info mca-origine-badge">Auto</span>}
                        </td>
                        <td>{m.descrizione || '—'}{m.numeroDocumento ? <span className="bop-text-muted"> · doc. {m.numeroDocumento}</span> : null}</td>
                        <td>{m.commessaNumero ? <span title={m.commessaDescrizione ?? ''}>{m.commessaNumero}</span> : '—'}</td>
                        <td>{fmtCompetenza(m.competenzaDal, m.competenzaAl)}</td>
                        <td>
                          {m.natura && (
                            <span className={`bop-badge ${m.natura === 'RICAVO' ? 'bop-badge--success' : 'bop-badge--warning'}`}>
                              {m.natura === 'RICAVO' ? 'Ricavo' : 'Costo'}
                            </span>
                          )}
                        </td>
                        <td className={`bop-text-right ${m.importo < 0 ? 'bop-text-danger' : ''}`}>{formatCurrency(m.importo)}</td>
                        <td className="bop-text-right">
                          <BopRowActions
                            actions={[
                              { icon: <RowIcon.Edit />, label: 'Modifica', onClick: () => openEdit(m), hidden: !canEdit || !isManuale },
                              { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => elimina(m), variant: 'danger', hidden: !canDelete || !isManuale },
                            ]}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={6} className="bop-text-right"><strong>Totale costi</strong></td>
                    <td className="bop-text-right"><strong>{formatCurrency(totali.costi)}</strong></td>
                    <td></td>
                  </tr>
                  <tr>
                    <td colSpan={6} className="bop-text-right"><strong>Totale ricavi</strong></td>
                    <td className="bop-text-right"><strong>{formatCurrency(totali.ricavi)}</strong></td>
                    <td></td>
                  </tr>
                  <tr>
                    <td colSpan={6} className="bop-text-right"><strong>Saldo (ricavi − costi)</strong></td>
                    <td className={`bop-text-right ${totali.saldo < 0 ? 'bop-text-danger' : ''}`}><strong>{formatCurrency(totali.saldo)}</strong></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </div>

      {modalOpen && (
        <Modal
          title={editId ? 'Modifica movimento' : 'Nuovo movimento'}
          onClose={closeModal}
          footer={
            <>
              <button className="bop-btn bop-btn-secondary" onClick={closeModal}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Salvo…' : 'Salva'}
              </button>
            </>
          }
        >
          {formError && <div className="bop-inline-error">{formError}</div>}

          {!editId && !isUnica && (
            <div className="bop-form-group">
              <label className="bop-label">Società *</label>
              <select
                className="bop-select"
                value={form.societaId}
                onChange={e => setForm(f => ({ ...f, societaId: e.target.value }))}
              >
                <option value="">Seleziona…</option>
                {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Conto *</label>
            <select
              className="bop-select"
              value={form.contoAnaliticoId}
              onChange={e => setForm(f => ({ ...f, contoAnaliticoId: e.target.value }))}
            >
              <option value="">Seleziona conto…</option>
              {contiLeaves.map(c => (
                <option key={c.id} value={c.id}>{c.codice} — {c.descrizione} ({c.natura === 'RICAVO' ? 'Ricavo' : 'Costo'})</option>
              ))}
            </select>
            {contoSelezionato && (
              <p className="mca-hint">
                Natura:{' '}
                <span className={`bop-badge ${contoSelezionato.natura === 'RICAVO' ? 'bop-badge--success' : 'bop-badge--warning'}`}>
                  {contoSelezionato.natura === 'RICAVO' ? 'Ricavo' : 'Costo'}
                </span>
              </p>
            )}
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Data *</label>
              <input className="bop-input" type="date" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Importo (€) *</label>
              <input
                className="bop-input"
                type="number"
                step="0.01"
                placeholder="0,00"
                value={form.importo}
                onChange={e => setForm(f => ({ ...f, importo: e.target.value }))}
              />
              <p className="mca-hint">Negativo solo per rettifiche/storni.</p>
            </div>
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Competenza dal</label>
              <input className="bop-input" type="date" value={form.competenzaDal} onChange={e => setForm(f => ({ ...f, competenzaDal: e.target.value }))} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Competenza al</label>
              <input className="bop-input" type="date" value={form.competenzaAl} onChange={e => setForm(f => ({ ...f, competenzaAl: e.target.value }))} />
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Descrizione / causale</label>
            <input
              className="bop-input"
              placeholder="es. Acquisto materiale di consumo"
              value={form.descrizione}
              onChange={e => setForm(f => ({ ...f, descrizione: e.target.value }))}
            />
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">N. documento</label>
              <input
                className="bop-input"
                placeholder="es. FT 123/2026"
                value={form.numeroDocumento}
                onChange={e => setForm(f => ({ ...f, numeroDocumento: e.target.value }))}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Commessa</label>
              <select
                className="bop-select"
                value={form.commessaId}
                onChange={e => setForm(f => ({ ...f, commessaId: e.target.value }))}
              >
                <option value="">Nessuna</option>
                {commesse.map(c => <option key={c.id} value={c.id}>{c.numero} — {c.descrizione}</option>)}
              </select>
            </div>
          </div>
        </Modal>
      )}

      {stampaOpen && societaId && (
        <PrintModal
          societaId={Number(societaId)}
          entitaType="movimenti_analitica"
          entitaId={Number(societaId)}
          entitaLabel="Movimenti contab. analitica"
          extraOpts={{
            ...(filtroDataDal ? { dataDal: filtroDataDal } : {}),
            ...(filtroDataAl ? { dataAl: filtroDataAl } : {}),
            ...(filtroNatura ? { natura: filtroNatura } : {}),
            ...(filtroContoId ? { contoAnaliticoId: Number(filtroContoId) } : {}),
            ...(filtroDivisioneId ? { divisioneId: Number(filtroDivisioneId) } : {}),
          }}
          onClose={() => setStampaOpen(false)}
        />
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
